import { __decorate } from "tslib";
import { Binding } from "@ortho-next/three-base";
import { Consts } from "../Utils/Consts.js";
/**
 * Attribute used to link AppModel field to BindedModel field.
 * If bindedModelKey is undefined it will link properties with same name.
 */
export function Link(bindedModelKey) {
    return function (target, propertyKey, descriptor) {
        !target._links && (target._links = []);
        target._links.push({ ak: propertyKey, bk: bindedModelKey });
    };
}
export var ApexSign;
(function (ApexSign) {
    ApexSign[ApexSign["valgus"] = 1] = "valgus";
    ApexSign[ApexSign["varus"] = -1] = "varus";
})(ApexSign || (ApexSign = {}));
export var ViewType;
(function (ViewType) {
    ViewType[ViewType["AP"] = 0] = "AP";
    ViewType[ViewType["LT"] = 1] = "LT";
})(ViewType || (ViewType = {}));
export var ViewStateType;
(function (ViewStateType) {
    ViewStateType[ViewStateType["AP"] = 0] = "AP";
    ViewStateType[ViewStateType["LT"] = 1] = "LT";
    ViewStateType[ViewStateType["multiple"] = 2] = "multiple";
})(ViewStateType || (ViewStateType = {}));
export var AxialRotationEnum;
(function (AxialRotationEnum) {
    AxialRotationEnum["external"] = "external";
    AxialRotationEnum["internal"] = "internal";
})(AxialRotationEnum || (AxialRotationEnum = {}));
/**
 * Model used by angular to create bindings. Only three.js part can set this values.
 */
export class AppModel {
    /**
     * Gets current state type.
     */
    get stateType() {
        return this._context.state.type;
    }
    /**
     * Gets current state data.
     */
    get stateDescription() {
        return this._context.state.stateDescription;
    }
    /**
     * Indicates if there are actions to undo.
     */
    get hasUndo() {
        return this._history.hasUndo;
    }
    /**
     * Indicates if there are actions to redo.
     */
    get hasRedo() {
        return this._history.hasRedo;
    }
    constructor(context, history) {
        this.isAppModel = true;
        /**
         * Gets measures.
         */
        this.measures = {};
        /**
         * Gets the femur axial rotation angle degrees.
         */
        this.def_femur_axialRotation = 0;
        /**
         * Gets the femur axial rotation angle sign.
         */
        this.def_femur_axialRotationSign = AxialRotationEnum.external;
        /**
         * Gets the tibia axial rotation angle degrees.
         */
        this.def_tibia_axialRotation = 0;
        /**
         * Gets the tbia axial rotation angle sign.
         */
        this.def_tibia_axialRotationSign = AxialRotationEnum.external;
        /**
         * Gets custom LPFA standard angle.
         */
        this.def_standardAngles_LPFA = Consts.paleyAngles.AP.mLPFA.standard;
        /**
         * Gets custom mLDFA standard angle.
         */
        this.def_standardAngles_mLDFA = Consts.paleyAngles.AP.mLDFA.standard;
        /**
         * Gets custom MPTA standard angle.
         */
        this.def_standardAngles_MPTA = Consts.paleyAngles.AP.MPTA.standard;
        /**
         * Gets custom LDTA standard angle.
         */
        this.def_standardAngles_LDTA = Consts.paleyAngles.AP.LDTA.standard;
        /**
         * Gets custom PPTA standard angle.
         */
        this.def_standardAngles_PPTA = Consts.paleyAngles.LT.PPTA.standard;
        /**
         * Gets custom ADTA standard angle.
         */
        this.def_standardAngles_ADTA = Consts.paleyAngles.LT.ADTA.standard;
        /**
         * Gets layers visibility values.
         */
        this.layers = {};
        /**
         * Gets layers enabled values.
         */
        this.layersValue = {};
        /**
         * Gets brightness of AP image.
         */
        this.brightnessAP = 0;
        /**
         * Gets brightness of Lateral image.
         */
        this.brightnessLT = 0;
        /**
         * Gets contrast of AP image.
         */
        this.contrastAP = 0;
        /**
         * Gets contrast of Lateral image.
         */
        this.contrastLT = 0;
        /**
         * Indicates if measurement tool is selected in AP view.
         */
        this.isMeasurementToolSelectedAP = false;
        /**
         * Indicates if measurement tool is selected in LT view.
         */
        this.isMeasurementToolSelectedLT = false;
        /**
         * Indicates if anatomical axis will sync translation and rotations.
         */
        this.syncAnatomicalAxis = false;
        /**
         * Indicates if apex is too far from axis in AP view.
         */
        this.apexTooFarAP = false;
        /**
         * Indicates if apex is too far from axis in LT view.
         */
        this.apexTooFarLT = false;
        /**
         * Indicates if osteotomy is too far in AP view.
         */
        this.ostTooFarAP = false;
        /**
         * Indicates if osteotomy is too far in LT view.
         */
        this.ostTooFarLT = false;
        this._context = context;
        this._history = history;
        this.executeLink();
    }
    executeLink() {
        if (this._links) {
            for (const keys of this._links) {
                const keyFrom = keys.bk ?? keys.ak;
                Binding.createCustom(`models_bind_${keyFrom}_${keys.ak}`, (m) => m[keyFrom], (value) => {
                    this[keys.ak] = value;
                }, [keyFrom]);
            }
        }
    }
}
__decorate([
    Link()
], AppModel.prototype, "syncAnatomicalAxis", void 0);
__decorate([
    Link()
], AppModel.prototype, "apexTooFarAP", void 0);
__decorate([
    Link()
], AppModel.prototype, "apexTooFarLT", void 0);
__decorate([
    Link()
], AppModel.prototype, "ostTooFarAP", void 0);
__decorate([
    Link()
], AppModel.prototype, "ostTooFarLT", void 0);
//# sourceMappingURL=AppModel.js.map